<template>
    <div class="comment" v-bind="$attrs">
        <template v-if="comment.deleted_at">
            <p class="comment__text comment__text--deleted">comment deleted</p>
        </template>
        <template v-else>
            <div class="comment__header">
                <BaseUser
                    v-if="comment.user"
                    class="comment__avatar"
                    :image="comment.user?.avatar?.conversions?.webp_256"
                    :subtitle="comment.user.full_name"
                />
                <span class="comment__date">{{ visibleDate }}</span>
            </div>
            <p class="comment__text">{{ comment.text }}</p>
            <div class="comment__actions">
                <button class="comment__action" @click="replyToComment">Reply</button>
                <button
                    v-if="comment.user && comment.user.id === authStore.user.id"
                    class="comment__action"
                    @click="deleteComment"
                >
                    Delete
                </button>
            </div>
        </template>
    </div>
    <Transition name="reply">
        <CommentInput
            v-if="replying"
            :video-id="comment.video.id"
            :comment-id="parent?.id ?? comment.id"
            focus-when-visible
            class="comment__reply-input"
            @blur="replying = false"
        />
    </Transition>
</template>

<script setup>
import DeleteCommentMutation from '~/graphql/mutations/deleteComment.graphql';
import { format, formatDistanceToNowStrict } from 'date-fns';
import { useCommentStore } from '~/store/comment';
const commentStore = useCommentStore();

const props = defineProps({
    comment: {
        type: Object,
        required: false,
        default: () => ({})
    },
    parent: {
        type: Object,
        required: false,
        default: () => ({})
    }
});

const authStore = useAuthStore();

const replying = ref(false);

const dateDiffInMs = computed(() => {
    return new Date() - new Date(props.comment.created_at);
});

const visibleDate = computed(() => {
    if (!props.comment.created_at) {
        return;
    }

    if (dateDiffInMs.value < 1000) {
        return 'Just now';
    } else if (dateDiffInMs.value < 2629746000) {
        return `${formatDistanceToNowStrict(props.comment.created_at)} ago`;
    } else {
        return format(props.comment.created_at, 'd MMM yyyy');
    }
});

function replyToComment() {
    replying.value = true;
}

const { execute: deleteCommentExecute } = useAsyncMutation(`delete-comment-${props.comment.id}`);

async function deleteComment() {
    if (confirm('Are you sure you want to delete this comment?')) {
        await deleteCommentExecute({
            mutation: DeleteCommentMutation,
            variables: {
                id: props.comment.id
            }
        });

        commentStore.fetchComments(props.comment.video.id);
    }
}
</script>

<style lang="less">
.comment--reply {
    margin-left: var(--spacing-lg);
}

.comment__header {
    .typography-label();
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);
}

.comment__text {
    padding: var(--spacing-xs) var(--spacing-sm);
    background-color: var(--color-dark-green-shade-400);
    border-radius: 0 var(--borderRadius-md) var(--borderRadius-md) var(--borderRadius-md);
    margin-bottom: var(--spacing-2xs);
    margin-top: var(--spacing-xs);

    @media @q-md-min {
        padding: var(--spacing-md);
    }
}

.comment__text--deleted {
    font-style: italic;
    opacity: .5;
    padding-top: var(--spacing-2xs);
    padding-bottom: var(--spacing-2xs);
    margin-top: var(--spacing-xs);
    margin-bottom: var(--spacing-sm);
}

.comment__actions {
    display: flex;
    justify-content: flex-end;
    gap: var(--spacing-xs);
}

.comment__action, .comment__date {
    .typography-label();
    color: var(--color-soft-blue-shade-600);
}

.comment__avatar {
    .base-user__subtitle {
        opacity: 1;
    }
}

.comment__action {
    .button-clean();
    color: var(--color-soft-blue-shade-600);
}

.base-user.comment__avatar {
    align-items: center;

    .base-avatar {
        margin-top: 0;
        height: 2rem;
    }

    .base-user__subtitle {
        .typography-label();
    }
}

.comment__reply-input {
    margin-top: var(--spacing-xs);
}

.reply-enter-active {
    transition: transform 0.5s ease, opacity 0.5s ease;
}

.reply-enter-from {
    opacity: 0;
    transform: translateY(.625rem);
}
</style>
